import React from 'react';
import { useThemeStore } from '../../stores';
import { getChartPalette } from './chartTheme';

export interface BreakdownRow {
  key: string;
  label: string;
  value: number;
  /** Optional secondary figure rendered to the right of the count. */
  meta?: string;
}

interface BreakdownBarsProps {
  rows: BreakdownRow[];
  /** Which palette role the bars take: neutral magnitude, or a loss/attrition measure. */
  tone?: 'primary' | 'danger';
  emptyMessage?: string;
  maxRows?: number;
}

/**
 * Ranked horizontal bars for a single measure — one hue, length carries magnitude.
 * Each row is directly labelled, so no legend and no tooltip is needed to read it.
 */
export const BreakdownBars: React.FC<BreakdownBarsProps> = ({
  rows,
  tone = 'primary',
  emptyMessage = 'No data for this period.',
  maxRows = 8,
}) => {
  const { theme } = useThemeStore();
  const colors = getChartPalette(theme === 'dark');

  const visible = rows.slice(0, maxRows);
  const max = Math.max(...visible.map((row) => row.value), 1);
  const barColor = tone === 'danger' ? colors.danger : colors.series[0];

  if (visible.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-secondary-500 dark:text-secondary-400">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {visible.map((row) => (
        <div key={row.key}>
          <div className="flex items-baseline justify-between gap-3 mb-1">
            <span className="text-sm text-secondary-700 dark:text-secondary-300 truncate" title={row.label}>
              {row.label}
            </span>
            <span className="flex items-baseline gap-2 shrink-0">
              <span className="text-sm font-semibold tabular-nums text-secondary-900 dark:text-secondary-50">
                {row.value.toLocaleString()}
              </span>
              {row.meta && (
                <span className="text-xs tabular-nums text-secondary-500 dark:text-secondary-400">
                  {row.meta}
                </span>
              )}
            </span>
          </div>
          <div className="h-2 w-full rounded-full" style={{ backgroundColor: colors.track }}>
            <div
              className="h-full rounded-full transition-[width] duration-500 ease-out"
              style={{
                width: `${Math.max((row.value / max) * 100, row.value > 0 ? 2 : 0)}%`,
                backgroundColor: barColor,
              }}
            />
          </div>
        </div>
      ))}

      {rows.length > maxRows && (
        <p className="pt-1 text-xs text-secondary-500 dark:text-secondary-400">
          + {rows.length - maxRows} more (included in the CSV export)
        </p>
      )}
    </div>
  );
};

export default BreakdownBars;
